import store from '@/store/index.js'
import { sasNeedsRefresh, createResourceUrl, blobSegmentsFromUrl } from '@/utils/SasHelpers'
import ApiServer from '@/utils/api/ApiServer'
import { BlobServiceClient } from '@azure/storage-blob'
import i18n from '@/i18n'
import { SasTokenLocations } from '@/utils/LocalStorageActor'
import { ATTACHMENT_MIMES, MAX_ATTACHMENT_FILES, uniqueFileName, validateAttachment } from '@/utils/Files'

export const validateTractFile = (file) => validateAttachment(file, f => ATTACHMENT_MIMES.has(f.type))

class TractFilesClient {
  async getContainerClient () {
    await this.initConnection()
    return this.containerClient
  }

  async getEndpoint () {
    return 'companyinfo/blobinfo/tractfiles'
  }

  async initConnection () {
    const sasCapsule = JSON.parse(localStorage.getItem(SasTokenLocations.TRACT_FILES_SAS))
    if (sasCapsule === null || sasNeedsRefresh(sasCapsule.data[0].blobSasUri)) {
      const endpoint = await this.getEndpoint()
      const response = await ApiServer.get(ApiServer.urlFor(endpoint))
      localStorage.setItem(SasTokenLocations.TRACT_FILES_SAS, JSON.stringify(response))
    }

    const storedResponse = JSON.parse(localStorage.getItem(SasTokenLocations.TRACT_FILES_SAS))
    this.container = storedResponse.data[0].container
    this.location = storedResponse.data[0].location
    this.client = new BlobServiceClient(storedResponse.data[0].blobSasUri)
    this.containerClient = this.client.getContainerClient(this.container)
  }

  getBlobLocation (tractId) {
    return `${this.location}/${tractId}/`
  }

  async getFiles (tract) {
    if (!tract) return []
    const files = []
    try {
      const containerClient = await this.getContainerClient()
      const listOptions = {
        includeMetadata: false,
        includeSnapshots: false,
        includeTags: false,
        includeVersions: false,
        prefix: this.getBlobLocation(tract.tractId)
      }
      const iterator = containerClient.listBlobsFlat(listOptions).byPage()
      const response = (await iterator.next()).value
      for (const blob of response.segment.blobItems) {
        const resourceUrl = createResourceUrl(JSON.parse(localStorage.getItem(SasTokenLocations.TRACT_FILES_SAS)).data[0].blobSasUri, this.container, blob.name)
        const resourceUrlTokens = resourceUrl.pathname.split('/')
        const filename = resourceUrlTokens.pop()
        const filetime = resourceUrlTokens.pop()
        const userId = resourceUrlTokens.pop()
        files.push({
          name: decodeURIComponent(filename),
          type: filename.split('.').pop(),
          user: userId,
          date: filetime,
          ref: resourceUrl.href
        })
      }
    } catch (e) {
      console.error(e)
      return []
    }
    return files
  }

  async deleteFile (fileUrl) {
    try {
      await this.initConnection()
      const [tractId, ...fileName] = blobSegmentsFromUrl(fileUrl, this.location)
      await ApiServer.delete(ApiServer.urlFor(`tracts/${tractId}/files/${fileName.map(encodeURIComponent).join('/')}`))
    } catch (e) {
      console.error(e)
    }
  }

  async uploadFile (tract, tractFile) {
    // Get files to check for maximum, duplicate names
    const files = await this.getFiles(tract)
    if (files.length >= MAX_ATTACHMENT_FILES) return
    const fileName = uniqueFileName(tractFile.name, files.map(f => f.name))
    const storedFileName = `${store.getters['user/userInfo'].applicationUserId}/${Date.now()}/${fileName}`
    const fileForUpload = new File([tractFile], storedFileName, { type: tractFile.type })

    try {
      const containerClient = await this.getContainerClient()
      const fullBlobLocation = await this.getBlobLocation(tract.tractId) + fileForUpload.name
      const blockBlobClient = await containerClient.getBlockBlobClient(fullBlobLocation)
      const options = {
        blobHTTPHeaders: {
          blobContentType: fileForUpload.type
        }
      }
      await blockBlobClient.uploadData(fileForUpload, options)
    } catch (e) {
      store.dispatch('snackbar/setSnackError', i18n.t('errorUploadingTractFile', { tractName: tract.name }))
      console.error(e)
    }
  }

  async uploadMigratedShapefile (tract, userId, tractFile) { // TODO MK: Temporary, remove when shapefile migration is removed
    const storedFileName = `${userId}/${Date.now()}/${tractFile.name}`
    const fileForUpload = new File([tractFile], storedFileName, { type: tractFile.type })
    try {
      const containerClient = await this.getContainerClient()
      const fullBlobLocation = this.getBlobLocation(tract.tractId) + fileForUpload.name
      const blockBlobClient = await containerClient.getBlockBlobClient(fullBlobLocation)
      const options = {
        blobHTTPHeaders: {
          blobContentType: fileForUpload.type
        }
      }
      await blockBlobClient.uploadData(fileForUpload, options)
    } catch (e) {
      console.error(e)
    }
  }
}

export const tractFilesClient = new TractFilesClient()
